import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { cn } from '@/lib/utils';
import {
  validateEmail,
  validatePassword,
  validateName,
  normalizeEmail,
  normalizeName,
  getPasswordStrength
} from '@/lib/validators';

import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useForceLightMode } from '../hooks/useForceLightMode';

export default function Register() {
  usePageTitle('Criar Conta');
  useForceLightMode();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { register, user, loading: authLoading } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const passwordStrength = password ? getPasswordStrength(password) : null;

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    // Mark all fields as touched
    setNameTouched(true);
    setEmailTouched(true);
    setPasswordTouched(true);

    // Validate all fields
    const nameValidation = validateName(name);
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    setNameError(nameValidation.error || '');
    setEmailError(emailValidation.error || '');
    setPasswordError(passwordValidation.error || '');

    if (!nameValidation.isValid || !emailValidation.isValid || !passwordValidation.isValid) {
      setLoading(false);
      return;
    }

    try {
      const normalizedEmail = normalizeEmail(email);
      const normalizedName = normalizeName(name);

      await register(normalizedEmail, password, normalizedName);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Register error:', err);
      const msg = err.message || '';
      if (msg.includes('already registered') || msg.includes('já registrado')) {
        setError(t('register.email_already_registered'));
      } else if (msg.includes('Password') || msg.includes('senha')) {
        setError(t('register.password_not_secure'));
      } else if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        setError('Sem conexão com a internet. Verifique sua rede e tente novamente.');
      } else if (msg.includes('timeout') || msg.includes('TIMEOUT')) {
        setError('O servidor demorou muito para responder. Tente novamente em alguns segundos.');
      } else {
        setError(msg || t('register.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameTouched) {
      const validation = validateName(value);
      setNameError(validation.error || '');
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailTouched) {
      const validation = validateEmail(value);
      setEmailError(validation.error || '');
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordTouched) {
      const validation = validatePassword(value);
      setPasswordError(validation.error || '');
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <SEO
        title="Criar conta grátis, Cadastre-se"
        description="Crie sua conta gratuita no Atas Digitais e comece a documentar suas reuniões de obra de forma profissional. Sistema de atas digitais para construtoras e fiscalizadoras."
        keywords="criar conta atas digitais, cadastro atas digitais, registro ata de reunião, sistema atas grátis"
        canonical="/register"
      />

      <div className="flex w-full flex-col lg:flex-row">
        <div className="relative hidden flex-1 overflow-hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-[#001A72] via-[#013EFB] to-[#0A7AFF]" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            {/* Logo fora do Card */}
            <img
              src="/logo-stant.svg"
              alt="Stant"
              className="mx-auto w-44 h-auto mb-8"
            />

            {/* Language Switcher */}
            <div className="absolute top-4 right-4">
              <LanguageSwitcher variant="compact" />
            </div>

            <Card className="bg-white/90 shadow-xl shadow-black/5 backdrop-blur-sm">
              <CardHeader className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">{t('register.title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('register.fill_data')}
                </p>
              </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('register.name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    autoComplete="name"
                    placeholder={t('register.name_placeholder')}
                    value={name}
                    onChange={(event) => handleNameChange(event.target.value)}
                    onBlur={() => {
                      setNameTouched(true);
                      const validation = validateName(name);
                      setNameError(validation.error || '');
                    }}
                    required
                    disabled={loading}
                    className={cn(
                      'h-11 transition-transform duration-200 ease-out focus-visible:scale-[1.01]',
                      nameError && nameTouched ? 'border-primary focus-visible:ring-primary' : ''
                    )}
                  />
                  {nameError && nameTouched && (
                    <div className="flex items-start gap-1.5 text-sm text-primary">
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{nameError}</span>
                    </div>
                  )}
                  {!nameError && nameTouched && name && (
                    <div className="flex items-start gap-1.5 text-sm text-green-600">
                      <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{t('register.name_valid')}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('register.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(event) => handleEmailChange(event.target.value)}
                    onBlur={() => {
                      setEmailTouched(true);
                      const validation = validateEmail(email);
                      setEmailError(validation.error || '');
                    }}
                    required
                    disabled={loading}
                    className={cn(
                      'h-11 transition-transform duration-200 ease-out focus-visible:scale-[1.01]',
                      emailError && emailTouched ? 'border-primary focus-visible:ring-primary' : ''
                    )}
                  />
                  {emailError && emailTouched && (
                    <div className="flex items-start gap-1.5 text-sm text-primary">
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{emailError}</span>
                    </div>
                  )}
                  {!emailError && emailTouched && email && (
                    <div className="flex items-start gap-1.5 text-sm text-green-600">
                      <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{t('register.email_valid')}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('register.password')}</Label>

                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={t('register.password_placeholder')}
                      value={password}
                      onChange={(event) => handlePasswordChange(event.target.value)}
                      onBlur={() => {
                        setPasswordTouched(true);
                        const validation = validatePassword(password);
                        setPasswordError(validation.error || '');
                      }}
                      required
                      disabled={loading}
                      minLength={8}
                      className={cn(
                        'h-11 pr-12 transition-transform duration-200 ease-out focus-visible:scale-[1.01]',
                        passwordError && passwordTouched ? 'border-primary focus-visible:ring-primary' : ''
                      )}
                    />

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setShowPassword((value) => !value)}
                      disabled={loading}
                      aria-pressed={showPassword}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-transform duration-200 hover:scale-105"
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </Button>
                  </div>

                  {/* Password requirements */}
                  {passwordTouched && password && (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">
                        {t('register.password_requirements')}
                      </div>
                      <div className="space-y-1">
                        <div
                          className={cn(
                            'flex items-center gap-1.5 text-xs',
                            password.length >= 8 ? 'text-green-600' : 'text-muted-foreground'
                          )}
                        >
                          {password.length >= 8 ? (
                            <CheckCircle2 size={14} />
                          ) : (
                            <AlertCircle size={14} />
                          )}
                          <span>{t('register.password_min_chars')}</span>
                        </div>
                        <div
                          className={cn(
                            'flex items-center gap-1.5 text-xs',
                            /[A-Z]/.test(password) ? 'text-green-600' : 'text-muted-foreground'
                          )}
                        >
                          {/[A-Z]/.test(password) ? (
                            <CheckCircle2 size={14} />
                          ) : (
                            <AlertCircle size={14} />
                          )}
                          <span>{t('register.password_uppercase')}</span>
                        </div>
                        <div
                          className={cn(
                            'flex items-center gap-1.5 text-xs',
                            /[a-z]/.test(password) ? 'text-green-600' : 'text-muted-foreground'
                          )}
                        >
                          {/[a-z]/.test(password) ? (
                            <CheckCircle2 size={14} />
                          ) : (
                            <AlertCircle size={14} />
                          )}
                          <span>{t('register.password_lowercase')}</span>
                        </div>
                        <div
                          className={cn(
                            'flex items-center gap-1.5 text-xs',
                            /[0-9]/.test(password) ? 'text-green-600' : 'text-muted-foreground'
                          )}
                        >
                          {/[0-9]/.test(password) ? (
                            <CheckCircle2 size={14} />
                          ) : (
                            <AlertCircle size={14} />
                          )}
                          <span>{t('register.password_number')}</span>
                        </div>
                      </div>

                      {/* Password strength indicator */}
                      {passwordStrength && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-muted-foreground">
                              {t('register.password_strength')}
                            </span>
                            <span
                              className={cn(
                                'text-xs font-semibold',
                                passwordStrength.strength === 'fraca' && 'text-destructive',
                                passwordStrength.strength === 'média' && 'text-yellow-600',
                                passwordStrength.strength === 'forte' && 'text-primary',
                                passwordStrength.strength === 'muito-forte' && 'text-green-600'
                              )}
                            >
                              {passwordStrength.strength.toUpperCase()}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full transition-all duration-300',
                                passwordStrength.strength === 'fraca' && 'bg-destructive',
                                passwordStrength.strength === 'média' && 'bg-yellow-500',
                                passwordStrength.strength === 'forte' && 'bg-primary',
                                passwordStrength.strength === 'muito-forte' && 'bg-green-500'
                              )}
                              style={{ width: `${passwordStrength.score}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {passwordError && passwordTouched && (
                    <div className="mt-2 flex items-start gap-1.5 text-sm text-primary">
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{passwordError}</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div
                    role="alert"
                    className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive shadow-sm"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 w-full text-base font-semibold transition-all duration-300 ease-out hover:-translate-y-[1px] hover:shadow-lg disabled:hover:translate-y-0"
                >
                  {loading ? t('register.submitting') : t('register.submit')}
                </Button>
              </form>

              <div className="text-center text-sm text-muted-foreground">
                {t('register.already_have_account')}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate('/login')}
                  className="ml-1 font-semibold"
                >
                  {t('register.login')}
                </Button>
              </div>
            </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
